import { createApi } from '@reduxjs/toolkit/dist/query/react';

import {
  getBaseQuery,
  getListProvidesTags,
  getUrlParamsString,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
} from '../generic';
import * as endpoints from '../endpoints';
import { REDUX_CACHE, COMMUNITIES, USERS, HTTP_METHOD } from '../constants';
import { usersApi } from './usersApi';
import { ICommunity } from '../../../types/interfaces/community';
import { IUser } from '../../../types/interfaces/user';
import { IAppFile } from '../../../types/interfaces/app-file';

export const communitiesApi = createApi({
  reducerPath: 'communitiesApi',
  tagTypes: [
    USERS.ME,
    COMMUNITIES.COMMUNITIES,
    COMMUNITIES.SELECTED_COMMUNITY,
    COMMUNITIES.COMMUNITY_SUBSCRIBERS,
    COMMUNITIES.COMMUNITY_REQUESTS,
    COMMUNITIES.MY_COMMUNITIES,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getCommunitiesApi: builder.query<
      ICommunity[],
      { searchValue: string; pageSize: number; page: number }
    >({
      keepUnusedDataFor: 3600,
      query({ searchValue, pageSize, page }) {
        const url =
          endpoints.Community.ROOT +
          '?' +
          getUrlParamsString({
            searchValue: searchValue,
            pageSize: pageSize,
            page: page,
          });

        return {
          url,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, COMMUNITIES.COMMUNITIES),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${JSON.stringify(queryArgs)}`;
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getMyCommunitiesApi: builder.query<ICommunity[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Community.MY,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, COMMUNITIES.MY_COMMUNITIES),
      ...TransformedErrorResponseWithNotification(),
    }),
    getCommunitySubscribersApi: builder.query<IUser[], { communityId: string }>(
      {
        keepUnusedDataFor: 3600,
        query({ communityId }) {
          return {
            url: endpoints.Community.SUBSCRIBERS.replace('${1}', communityId),
            method: HTTP_METHOD.GET,
          };
        },
        providesTags: (result) =>
          getListProvidesTags(result, COMMUNITIES.COMMUNITY_SUBSCRIBERS),
        ...TransformedErrorResponseWithNotification(),
      },
    ),
    getCommunityByIdApi: builder.query<ICommunity, string>({
      keepUnusedDataFor: 3600,
      query(id: string) {
        return {
          url: `${endpoints.Community.ROOT}/${id}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: COMMUNITIES.SELECTED_COMMUNITY }],
      ...TransformedErrorResponseWithNotification(),
    }),
    getCommunityRequestsApi: builder.query<IUser[], string>({
      keepUnusedDataFor: 300,
      query(id: string) {
        return {
          url: endpoints.Community.REQUESTS.replace('${1}', id),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: COMMUNITIES.COMMUNITY_REQUESTS }],
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutCommunityApi: builder.mutation<
      void,
      { id: string; body: Partial<ICommunity> & { avatar?: IAppFile } }
    >({
      query({ id, body }) {
        return {
          url: endpoints.Community.ROOT + (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      async onQueryStarted({ ...patch }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags([USERS.USERS]));
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: COMMUNITIES.COMMUNITIES,
              id: REDUX_CACHE.LIST,
            },
            {
              type: COMMUNITIES.SELECTED_COMMUNITY,
            },
            {
              type: COMMUNITIES.COMMUNITIES,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Community'),
    }),
    subscribeToCommunityApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: endpoints.Community.SUBSCRIBE.replace('${1}', id),
          method: HTTP_METHOD.POST,
        };
      },
      async onQueryStarted({ ...patch }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags([USERS.ME]));
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: COMMUNITIES.COMMUNITIES,
              id: REDUX_CACHE.LIST,
            },
            {
              type: COMMUNITIES.SELECTED_COMMUNITY,
            },
            {
              type: COMMUNITIES.COMMUNITIES,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    unsubscribeFromCommunityApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: endpoints.Community.UNSUBSCRIBE.replace('${1}', id),
          method: HTTP_METHOD.POST,
        };
      },
      async onQueryStarted({ ...patch }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags([USERS.ME]));
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: COMMUNITIES.COMMUNITIES,
              id: REDUX_CACHE.LIST,
            },
            {
              type: COMMUNITIES.SELECTED_COMMUNITY,
            },
            {
              type: COMMUNITIES.COMMUNITIES,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    changeCommunitySubscriptionStatusApi: builder.mutation<
      void,
      { id: string; body: { status: string; userId: string } }
    >({
      query({ id, body }) {
        return {
          url: endpoints.Community.STATUS.replace('${1}', id),
          method: HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: COMMUNITIES.COMMUNITIES,
              id: REDUX_CACHE.LIST,
            },
            {
              type: COMMUNITIES.COMMUNITY_SUBSCRIBERS,
            },
            {
              type: COMMUNITIES.SELECTED_COMMUNITY,
            },
            {
              type: COMMUNITIES.COMMUNITY_REQUESTS,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    deleteCommunityApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.Community.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: COMMUNITIES.COMMUNITIES,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Community'),
    }),
  }),
});

export const {
  useGetCommunitiesApiQuery: useGetCommunities,
  useGetMyCommunitiesApiQuery: useGetMyCommunities,
  useLazyGetCommunitiesApiQuery: useLazyGetCommunities,
  useGetCommunityByIdApiQuery: useGetCommunity,
  useGetCommunityRequestsApiQuery: useGetCommunityRequests,
  useLazyGetCommunityByIdApiQuery: useGetCommunityByIdLazy,
  useGetCommunitySubscribersApiQuery: useGetCommunitySubscribers,
  usePostPutCommunityApiMutation: usePostPutCommunity,
  useSubscribeToCommunityApiMutation: useSubscribeToCommunity,
  useUnsubscribeFromCommunityApiMutation: useUnsubscribeFromCommunity,
  useDeleteCommunityApiMutation: useDeleteCommunity,
  useChangeCommunitySubscriptionStatusApiMutation:
    useChangeCommunitySubscriptionStatus,
} = communitiesApi;
